import type { ReactNode } from 'react';

interface AttachmentLike {
  id: number;
  title?: string;
  fullPath?: string;
  contentType?: string;
}

// Email-imported notes & descriptions contain markers like:
//   [cid:image001.png@01DCED0D.31F2B660]
// where the part before `@` is the original filename. The matching binary lives
// as a TicketAttachment whose `title` is "image001.png" (the host-id after `@`
// is an Outlook-generated suffix we can't correlate with directly).
//
// IMPORTANT: a /g regex carries mutable `lastIndex` state, which silently breaks
// when the same regex is reused across nested renderWithCidImages calls during a
// single React render (description + N notes). Build a fresh regex per call.
const CID_PATTERN = '\\[cid:([^@\\]]+)(?:@[^\\]]+)?\\]';

function stripExt(s: string): string {
  return s.toLowerCase().replace(/\.[^.]+$/, '');
}

function isImageAttachment(a: AttachmentLike): boolean {
  if ((a.contentType ?? '').toLowerCase().startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|svg|bmp|avif)(\?|$)/i.test(a.title ?? a.fullPath ?? '');
}

function findAttachment(
  filename: string,
  attachments: AttachmentLike[],
  consumed: Set<number>,
): AttachmentLike | undefined {
  const lower = filename.toLowerCase();
  const base = stripExt(lower);

  // 1) Exact filename match (with or without extension), case-insensitive.
  //    Handles Outlook-style "image001.png" cid markers where Autotask stored
  //    the file with the same name.
  const exactCandidates = attachments
    .map((a) => ({
      a,
      titleLower: (a.title ?? '').toLowerCase(),
      pathLower: (a.fullPath ?? '').toLowerCase(),
    }))
    .filter(({ titleLower, pathLower }) =>
      titleLower === lower
      || pathLower === lower
      || stripExt(titleLower) === base
      || stripExt(pathLower) === base,
    );
  const exactImage = exactCandidates.find(({ a }) => isImageAttachment(a));
  if (exactImage) return exactImage.a;
  if (exactCandidates[0]) return exactCandidates[0].a;

  // 2) Substring match — useful when the CID UUID is part of the stored filename
  //    (e.g. some email gateways prepend it).
  const subMatch = attachments.find((a) => {
    const t = (a.title ?? '').toLowerCase();
    const p = (a.fullPath ?? '').toLowerCase();
    return t.includes(lower) || p.includes(lower) || (base.length >= 4 && (t.includes(base) || p.includes(base)));
  });
  if (subMatch) return subMatch;

  // 3) Fallback: nth unconsumed image attachment in document order. Email-imported
  //    inline images use UUID Content-IDs that Autotask doesn't preserve, so the
  //    only way to associate cid markers with the right binary is to pair them up
  //    sequentially with the available image attachments.
  return attachments.find((a) => !consumed.has(a.id) && isImageAttachment(a));
}

export function renderWithCidImages(text: string | null | undefined, attachments: AttachmentLike[]): ReactNode[] {
  if (!text) return [];
  // Fresh regex per call so concurrent renderWithCidImages calls don't trample lastIndex.
  const re = new RegExp(CID_PATTERN, 'g');
  // Tracks which attachments we've already used so the sequential fallback can pick the
  // next unused image instead of reassigning the same one to every UUID-style cid.
  const consumed = new Set<number>();
  const out: ReactNode[] = [];
  let lastIdx = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIdx) out.push(text.slice(lastIdx, match.index));
    const filename = match[1].trim();
    const att = findAttachment(filename, attachments, consumed);
    if (att) {
      consumed.add(att.id);
      out.push(
        <img
          key={`cid-${key++}`}
          src={`/api/attachments/${att.id}/data`}
          alt={filename}
          loading="lazy"
          className="my-2 block max-h-96 max-w-full rounded border bg-muted/20 object-contain"
        />,
      );
    } else {
      out.push(
        <span key={`cid-${key++}`} className="mx-0.5 inline-block rounded bg-muted px-1 py-0.5 align-middle text-xs text-muted-foreground">
          image: {filename}
        </span>,
      );
    }
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
}
